import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppDispatch, useAppSelector } from '../../store';

// PACKAGES
import { useParams } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import ReactPlayer from 'react-player';

// COMPONENTS
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';
import GlobalModal from '../../components/GlobalModal';
import CompleteModal from './CompleteModal';

// TYPES
import { TrainingViewTrainingModule } from '../../api/models';
import { RouteParams } from '../../Interfaces';

const TrainingDetail: React.FC = () => {
	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Get Id from params
	const params = useParams<RouteParams>();
	const trainingId = params.id;

	// Set up Data state and initial Data
	const initialData = {
		id: undefined,
		subtitle: undefined,
		title: undefined,
		overview: undefined,
		videoEmbedCode: undefined,
		isAcknowledged: undefined,
		canAcknowledge: undefined
	};

	const [trainingDetailData, setTrainingDetailData] = React.useState<TrainingViewTrainingModule>(initialData);

	const getTrainingDetailData = async () => {
		setIsLoading(true);

		const response = await apibridge.getTrainingView(trainingId);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = initialData } = response;

			setIsLoading(false);

			setTrainingDetailData(result);
		}
	};

	React.useEffect(() => {
		getTrainingDetailData();
	}, []);

	const { subtitle, title, overview, videoEmbedCode, isAcknowledged, canAcknowledge, standards, acknowledgementId } =
		trainingDetailData;

	// State to control Modal
	const [showCompleteModal, setShowCompleteModal] = React.useState<boolean>(false);

	return (
		<div className="page-general-layout wide-gap training-view">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<div className="page-heading">
									<p className="sub-grey-text mbtom-0">Child Safe Standard</p>

									<p className="sub-purple-text">
										{standards &&
											standards.length > 0 &&
											standards.map((standard, index) => {
												if (index + 1 === standards.length) {
													return standard;
												} else {
													return standard + ', ';
												}
											})}
									</p>

									<h1 className="h1">{title}</h1>
								</div>

								<p className="sub-grey-text mbtom-48">Abiding by {title}</p>

								<ReactPlayer className="embed-video" url={videoEmbedCode} controls />

								<div className="overview">
									<h3>Overview</h3>

									<p>{overview} </p>
								</div>

								{isOrganisationActive && (
									<div className="single-btn-right-container">
										<Button
											className="btn-primary btn-right"
											disabled={!canAcknowledge || isAcknowledged}
											onClick={() => setShowCompleteModal(true)}
										>
											Mark as complete
										</Button>
									</div>
								)}

								<GlobalModal isOpen={showCompleteModal} setIsOpen={setShowCompleteModal}>
									<CompleteModal title={title} acknowledgementId={acknowledgementId} />
								</GlobalModal>
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default TrainingDetail;
